type FetchHeaders = { [key: string]: string };

type FetchOptions = {
  method: string;
  headers: FetchHeaders;
  body?: string;
  keepalive?: boolean;
};

type FetchRequestOptions = {
  keepalive?: boolean;
};

import { identifyEnvironment } from "@/utils/IdentifyGlobalEnv";

const environmentApiDictionary = {
  local: "https://ai-assistant-v2.staging.feegow.com/api",
  staging: "https://ai-assistant-v2.staging.feegow.com/api",
  production: "https://ai-assistant-v2.feegow.com/api",
};

function getEnvironmentBaseUrl(): string {
  const env = identifyEnvironment();
  return environmentApiDictionary[env];
}

export class FetchApi {
  protected baseUrl: string;
  protected requestHeaders: FetchHeaders = {};
  constructor(baseUrl?: string, requestHeaders?: FetchHeaders) {
    this.baseUrl = baseUrl || getEnvironmentBaseUrl();
    this.requestHeaders = requestHeaders || {};
  }

  setBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl;
  }
  setHeaders(headers: FetchHeaders) {
    this.requestHeaders = headers;
  }

  /**
   * Fetch data from the Feegow API
   * @param method HTTP method
   * @param route API route
   * @param bodyOrParams
   * @param returnType
   */
  async fetch<T>(
    method: string,
    route: string,
    bodyOrParams?: object,
    returnType?: string,
    // eslint-disable-next-line comma-dangle
    requestOptions?: FetchRequestOptions
  ): Promise<T> {
    const apiUrl = this.baseUrl || "";
    const options: FetchOptions = { method, headers: this.requestHeaders, keepalive: requestOptions?.keepalive };
    let queryParam = "";

    if (method === "GET" && bodyOrParams) {
      const formBody: string[] = [];
      for (const property in bodyOrParams) {
        const encodedKey = encodeURIComponent(property);
        const encodedValue = encodeURIComponent(bodyOrParams[property]);
        formBody.push(`${encodedKey}=${encodedValue}`);
      }
      queryParam = `?${formBody.join("&")}`;
    } else if (bodyOrParams) {
      options.body = JSON.stringify(bodyOrParams);
      options.headers = {
        ...this.requestHeaders,
        "Content-Type": "application/json",
      };
    }

    const response = await fetch(`${apiUrl}${route}${queryParam}`, options);

    if (!response.ok) {
      throw new Error(`Fetch failed with statusCode ${response.status}`);
    }
    if (response.status === 204) {
      return undefined as T;
    }
    if (returnType == "text") {
      return (await response.text()) as unknown as T;
    }

    return await response.json();
  }

  async get<T>(route: string, body?: object, returnType?: string): Promise<T> {
    return await this.fetch<T>("GET", route, body, returnType);
  }
  async post<T>(route: string, body?: object, returnType?: string): Promise<T> {
    return await this.fetch<T>("POST", route, body, returnType);
  }

  async put<T>(route: string, body?: object, returnType?: string): Promise<T> {
    return await this.fetch<T>("PUT", route, body, returnType);
  }

  async delete<T>(route: string, body?: object, returnType?: string): Promise<T> {
    return await this.fetch<T>("DELETE", route, body, returnType);
  }

  async patch<T>(route: string, body?: object, returnType?: string): Promise<T> {
    return await this.fetch<T>("PATCH", route, body, returnType);
  }
}
